import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationEntry, TranslationsController } from '../api/translations/translations.controller';

type ContextValue = {
  translate: (key: string) => string;
  refresh: () => Promise<void>;
};

const ApiTranslationsContext = createContext<ContextValue>({ translate: () => '', refresh: async () => {} });

export const ApiTranslationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<TranslationEntry[]>([]);
  const { i18n } = useTranslation();

  const loadEntries = useCallback(async () => {
    try {
      const data = await TranslationsController.getTranslations();
      setEntries(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const language = i18n.language?.split('-')[0] || 'pl';

  const translate = useCallback((key: string): string => {
    const entry = entries.find(e => e.key === key && e.language === language)
      ?? entries.find(e => e.key === key);
    return entry?.value ?? '';
  }, [entries, language]);

  return (
    <ApiTranslationsContext.Provider value={{ translate, refresh: loadEntries }}>
      {children}
    </ApiTranslationsContext.Provider>
  );
};

export const useApiTranslations = () => useContext(ApiTranslationsContext);
